/**
 * PUCK-8: Responsive controls hook — breakpoint state and per-component visibility toggles.
 */

import { useState, useCallback, useEffect, useMemo } from "react";

export type Breakpoint = "desktop" | "tablet" | "mobile";

const BREAKPOINT_WIDTHS: Record<Breakpoint, number> = {
  desktop: 1280,
  tablet: 768,
  mobile: 375,
};

interface VisibilityMap {
  readonly [componentId: string]: {
    readonly desktop: boolean;
    readonly tablet: boolean;
    readonly mobile: boolean;
  };
}

interface ResponsiveControlsState {
  readonly activeBreakpoint: Breakpoint;
  readonly previewWidth: number;
  readonly visibility: VisibilityMap;
  readonly setBreakpoint: (bp: Breakpoint) => void;
  readonly toggleVisibility: (componentId: string, bp: Breakpoint) => void;
  readonly isVisible: (componentId: string) => boolean;
}

export function useResponsiveControls(
  initialBreakpoint: Breakpoint = "desktop",
): ResponsiveControlsState {
  const [activeBreakpoint, setActiveBreakpoint] = useState<Breakpoint>(initialBreakpoint);
  const [visibility, setVisibility] = useState<VisibilityMap>({});

  const previewWidth = useMemo(
    () => BREAKPOINT_WIDTHS[activeBreakpoint],
    [activeBreakpoint],
  );

  const setBreakpoint = useCallback((bp: Breakpoint) => {
    setActiveBreakpoint(bp);
  }, []);

  const toggleVisibility = useCallback(
    (componentId: string, bp: Breakpoint) => {
      setVisibility((prev) => {
        const current = prev[componentId] ?? {
          desktop: true,
          tablet: true,
          mobile: true,
        };
        return {
          ...prev,
          [componentId]: {
            ...current,
            [bp]: !current[bp],
          },
        };
      });
    },
    [],
  );

  const isVisible = useCallback(
    (componentId: string): boolean => {
      const config = visibility[componentId];
      if (!config) return true;
      return config[activeBreakpoint];
    },
    [visibility, activeBreakpoint],
  );

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!e.altKey) return;

      switch (e.key) {
        case "1":
          setActiveBreakpoint("desktop");
          break;
        case "2":
          setActiveBreakpoint("tablet");
          break;
        case "3":
          setActiveBreakpoint("mobile");
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  return {
    activeBreakpoint,
    previewWidth,
    visibility,
    setBreakpoint,
    toggleVisibility,
    isVisible,
  };
}

export { BREAKPOINT_WIDTHS };
